import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LandingHeader from './common/LandingHeader';
import LandingFooter from './common/LandingFooter';
import './LandingPage.css';

// The two client-facing features that set Dress Desk apart from back-office-only tools.
// Each key resolves to landing.<key>Badge/Title/Description/Point1..3 in the locale files.
const SPOTLIGHTS = [
  { key: 'spotlight1', icon: '✨' },
  { key: 'spotlight2', icon: '🌐' }
];

// Back-office capabilities, in landing.feature<n>Title/Description.
const FEATURES = [
  { n: 1, icon: '👗' },   // dress inventory
  { n: 2, icon: '📅' },   // appointments
  { n: 3, icon: '💰' },   // finances
  { n: 4, icon: '📊' },   // analytics
  { n: 5, icon: '📋' },   // contracts
  { n: 6, icon: '👥' },   // team
  { n: 7, icon: '👜' },   // accessories
  { n: 8, icon: '🏷️' },   // brands & lookbooks
  { n: 9, icon: '🕒' },   // staff hours & time off
  { n: 10, icon: '🧵' },  // care labels & declarations
  { n: 11, icon: '📜' },  // change history
  { n: 12, icon: '🌍' }   // languages & currencies
];

const LandingPage = () => {
  const { t } = useLanguage();

  return (
    <div className="landing-page">
      <LandingHeader />

      {/* Hero Section */}
      <section className="hero-section" style={{ backgroundImage: `linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%), url(${process.env.PUBLIC_URL}/hero.jpg)` }}>
        <div className="landing-container">
          <div className="hero-content">
            <h1 className="hero-title">{t('landing.heroTitle')}</h1>
            <p className="hero-subtitle">{t('landing.heroSubtitle')}</p>
            <div className="hero-buttons">
              <Link to="/register" className="landing-button large primary">
                {t('landing.startFreeTrial')}
              </Link>
              <a href="#features" className="landing-button large secondary">
                {t('landing.learnMore')}
              </a>
            </div>
            <p className="hero-note">
              {t('landing.freeTrialDays')} · {t('landing.noCreditCard')}
            </p>
          </div>
        </div>
      </section>

      {/* Spotlight Section - the two client-facing differentiators */}
      <section id="features" className="spotlight-section">
        <div className="landing-container">
          <h2 className="section-title">{t('landing.spotlightTitle')}</h2>
          <p className="section-subtitle">{t('landing.spotlightSubtitle')}</p>

          <div className="spotlight-grid">
            {SPOTLIGHTS.map(({ key, icon }) => (
              <article className="spotlight-card" key={key}>
                <div className="spotlight-header">
                  <span className="spotlight-icon" aria-hidden="true">{icon}</span>
                  <span className="spotlight-badge">{t(`landing.${key}Badge`)}</span>
                </div>
                <h3>{t(`landing.${key}Title`)}</h3>
                <p className="spotlight-description">{t(`landing.${key}Description`)}</p>
                <ul className="spotlight-points">
                  {[1, 2, 3].map((n) => (
                    <li key={n}>
                      <span className="benefit-icon" aria-hidden="true">✓</span>
                      {t(`landing.${key}Point${n}`)}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="landing-container">
          <h2 className="section-title">{t('landing.featuresTitle')}</h2>
          <p className="section-subtitle">{t('landing.featuresSubtitle')}</p>

          <div className="features-grid">
            {FEATURES.map(({ n, icon }) => (
              <div className="feature-card" key={n}>
                <div className="feature-icon" aria-hidden="true">{icon}</div>
                <h3>{t(`landing.feature${n}Title`)}</h3>
                <p>{t(`landing.feature${n}Description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="landing-container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2>{t('landing.benefitsTitle')}</h2>
              <ul className="benefits-list">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <li key={n}>
                    <span className="benefit-icon" aria-hidden="true">✓</span>
                    {t(`landing.benefit${n}`)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="benefits-image">
              <img 
                src={`${process.env.PUBLIC_URL}/Selena2.jpg`} 
                alt="Bridal Shop"
                className="benefits-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="landing-container">
          <div className="cta-content">
            <h2>{t('landing.ctaTitle')}</h2>
            <p>{t('landing.ctaSubtitle')}</p>
            <div className="cta-buttons">
              <Link to="/register" className="landing-button large primary">
                {t('landing.createAccount')}
              </Link>
              <Link to="/login" className="landing-button large secondary">
                {t('landing.signInNow')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default LandingPage;

